import { registerLocaleData, CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  signal,
  LOCALE_ID,
  DestroyRef,
} from '@angular/core';
import esCo from '@angular/common/locales/es-CO';
import { PageTitleService } from '../../shared/services';
import { Service, ServiceInput, Professional } from '../../shared/interfaces';
import { ServiceAdd } from './service-add/service-add';
import { Modal } from '../../shared/components/modal/modal';
import { CreatesByBusiness } from '../../shared/common/CreatesByBusiness.class';
import { ConfirmModal } from '../../shared/components/confirm-modal/confirm-modal';
import {
  DurationOption,
  SERVICE_DURATIONS,
} from '../../config/serviceDurations';
import { debounceTime, distinctUntilChanged, Subject, tap, forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpContext } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';
import { switchMap } from 'rxjs/operators';
import { HasAnyDirective } from '../../shared/directives/has-any.directive';
import { Router } from '@angular/router';
registerLocaleData(esCo);

const currencyFormatter = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

@Component({
  selector: 'app-services',
  imports: [CommonModule, Modal, ConfirmModal, HasAnyDirective],
  templateUrl: './services.html',
  styleUrl: './services.css',
  providers: [{ provide: LOCALE_ID, useValue: 'es-CO' }],
})
export class Services
  extends CreatesByBusiness<Service | any, ServiceInput>
  implements OnInit
{
  services: Service[] = [];
  loading = false;
  page = 1;
  pageSize = 20;
  totalCount = 0;
  searchTerm = '';
  totalPages = 1;
  titleService = inject(PageTitleService);
  protected endpoint = 'services';
  ServiceAddForm = ServiceAdd;
  confirmOpen = false;
  toDeleteId: string | null = null;
  toDeleteName: string | null = null;
  modalTitle = 'Crear servicio';
  modalInputs: Record<string, unknown> = {};
  durationOptions: DurationOption[] = SERVICE_DURATIONS;
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<string>();
  private http = inject(HttpClient);
  private apiBaseUrl = environment.apiUrl;
  private router = inject(Router);

  // Role-based permissions
  canCreate = this.authService.hasAny(['services.create']);
  canEdit = this.authService.hasAny(['services.update']);
  canDelete = this.authService.hasAny(['services.delete']);

  // Professionals per service
  serviceProfessionalsMap = signal<Record<string, Professional[]>>({});
  professionalsLoaded = signal(false);
  skeletonRows = Array(6).fill(0);

  /** Minutes represented by a full duration bar; grows if a service is longer than 4 h */
  durationScale = 240;

  constructor() {
    super();
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((q) => {
          this.searchTerm = q;
          this.page = 1;
          this.load();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onSearch = (q: string) => this.search$.next(q);

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Servicios',
      subtitle: 'Lo que ofreces, con su duración y precio',
      // Also reachable from the main menu; only show Configuración as parent when opened from there
      breadcrumbs: this.router.url.startsWith('/settings') ? [{ name: 'Configuración', url: '/settings' }] : [],
    });
    this.load();
  }

  onPageChange = (p: number) => {
    this.page = p;
    this.load();
  };

  load() {
    this.loading = true;
    this.preloader.show();
    this.businessService
      .getRecordsByBusinessId<Service>(
        this.businessId,
        {
          page: this.page,
          pageSize: this.pageSize,
          searchTerm: this.searchTerm,
          sort: 'createdAt',
          order: 'desc',
          isActive: true,
        },
        this.endpoint
      )
      .subscribe({
        next: (res: any) => {
          const data = (res.services ?? []).map((x: Service) => {
            return {
              ...x,
              durationLabel: this.durationOptions.find(
                (d) => d.value === x.durationMinutes
              )?.label,
            };
          });
          this.services = data;
          this.durationScale = Math.max(
            240,
            ...data.map((s: Service) => s.durationMinutes || 0)
          );
          this.page = res.page;
          this.pageSize = res.pageSize;
          this.totalCount = res.totalCount;
          this.totalPages = res.totalPages;
          this.loading = false;
          this.preloader.hide();
          this.loadProfessionalsForServices(data);
        },
        error: (err) => {
          console.error(err);
          this.preloader.hide();
        },
      });
  }

  loadProfessionalsForServices(services: Service[]) {
    const map: Record<string, Professional[]> = {};
    const requests = services.map((s) =>
      this.http
        .get<Professional[]>(
          `${this.apiBaseUrl}/api/v1/businesses/${this.businessId}/services/${s.serviceId}/professionals`,
          { context: new HttpContext().set(REQUIRE_AUTH, true) }
        )
    );
    this.professionalsLoaded.set(false);
    if (requests.length === 0) return;
    forkJoin(requests).subscribe({
      next: (results) => {
        results.forEach((professionals, idx) => {
          map[services[idx].serviceId] = professionals;
        });
        this.serviceProfessionalsMap.set(map);
        this.professionalsLoaded.set(true);
      },
      error: () => {
        this.serviceProfessionalsMap.set({});
      },
    });
  }

  getDurationLabel(minutes: number | undefined): string {
    if (!minutes) return '';
    const known = this.durationOptions.find((d) => d.value === minutes)?.label;
    if (known) return known;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
  }

  /** Width of a service's duration bar, relative to the longest bookable slot */
  durationPercent(minutes: number | undefined): number {
    if (!minutes) return 0;
    return Math.min(100, (minutes / this.durationScale) * 100);
  }

  /** Lowest and highest price on the current page, e.g. "$20.000 a $180.000" */
  get priceRange(): string {
    const prices = this.services.map((s) => Number(s.price) || 0);
    if (prices.length === 0) return '';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max
      ? this.formatPrice(min)
      : `${this.formatPrice(min)} a ${this.formatPrice(max)}`;
  }

  /** Services on the current page that nobody on the team can perform */
  get unstaffedCount(): number {
    if (!this.professionalsLoaded()) return 0;
    const map = this.serviceProfessionalsMap();
    return this.services.filter((s) => !map[s.serviceId]?.length).length;
  }

  professionalName(p: Professional): string {
    return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
  }

  formatPrice(value: number | string | undefined): string {
    const amount = typeof value === 'string' ? Number(value) : value;
    return currencyFormatter.format(amount ?? 0);
  }

  onCreateService() {
    this.open = true;
    this.modalTitle = 'Crear Servicio';
    this.modalInputs = {
      entityId: null,
    };
  }

  onEditService(serviceId: any) {
    this.modalTitle = 'Editar Servicio';
    this.modalInputs = {
      entityId: serviceId,
      endpoint: this.endpoint,
    };
    this.open = true;
  }

  openDelete(row: any) {
    this.toDeleteId = row.serviceId;
    this.toDeleteName = row.name || null;
    this.confirmOpen = true;
  }

  onConfirmDelete() {
    if (!this.toDeleteId) return;
    this.deleteRecordById(this.toDeleteId);
  }

  protected override afterDelete(): void {
    this.toastService.show('Servicio eliminado correctamente', 'success');
    this.load();
  }

  protected override afterCreate(_res: Service): void {
    this.load();
  }

  protected override afterUpdate(): void {
    this.toastService.show('Servicio actualizado correctamente', 'success');
    this.open = false;
    this.load();
  }

  override onModalConfirm(payload: Service | any): void {
    if (!payload) {
      this.open = false;
      return;
    }

    const professionalIds: string[] = payload.professionalIds || [];
    const serviceId = payload.entityId;
    const imageFile: File | null = payload.imageFile || null;

    // Remove professionalIds and imageFile from payload before sending to API
    const { professionalIds: _, imageFile: __, ...servicePayload } = payload;

    if (!serviceId) {
      // Create service
      this.isSaving = true;
      this.businessService
        .createRecordByBusinessId<Service>(this.businessId, servicePayload, this.endpoint)
        .pipe(
          switchMap((res) => {
            const newServiceId = (res as any).serviceId || (res as any).id;
            const operations: any[] = [];

            // Upload image if provided
            if (newServiceId && imageFile) {
              operations.push(
                this.businessService.uploadServiceImage(this.businessId, newServiceId, imageFile)
              );
            }

            // Add professionals if provided
            if (newServiceId && professionalIds.length > 0) {
              const addProfessionalRequests = professionalIds.map(professionalId =>
                this.http.post(
                  `${this.apiBaseUrl}/api/v1/businesses/${this.businessId}/services/${newServiceId}/professionals`,
                  { professionalId },
                  { context: new HttpContext().set(REQUIRE_AUTH, true) }
                )
              );
              operations.push(...addProfessionalRequests);
            }

            if (operations.length > 0) {
              return forkJoin(operations).pipe(
                switchMap(() => of(res))
              );
            }
            return of(res);
          })
        )
        .subscribe({
          next: (res) => {
            this.isSaving = false;
            this.open = false;
            this.afterCreate(res as Service);
          },
          error: (err) => {
            this.isSaving = false;
            this.onCreateError(err);
          },
        });
    } else {
      // Update service
      this.isSaving = true;

      // First, get current professionals
      this.http
        .get<any[]>(
          `${this.apiBaseUrl}/api/v1/businesses/${this.businessId}/services/${serviceId}/professionals`,
          { context: new HttpContext().set(REQUIRE_AUTH, true) }
        )
        .pipe(
          switchMap((currentProfessionals) => {
            const currentIds = currentProfessionals.map((p) => p.userId);
            const toAdd = professionalIds.filter(id => !currentIds.includes(id));
            const toRemove = currentIds.filter(id => !professionalIds.includes(id));

            // Update service
            return this.businessService
              .updateRecordByBusinessId<ServiceInput>(
                this.businessId,
                serviceId,
                servicePayload,
                this.endpoint
              )
              .pipe(
                switchMap(() => {
                  const operations: any[] = [];

                  // Upload image if provided
                  if (imageFile) {
                    operations.push(
                      this.businessService.uploadServiceImage(this.businessId, serviceId, imageFile)
                    );
                  }

                  // Add new professionals
                  const addRequests = toAdd.map(professionalId =>
                    this.http.post(
                      `${this.apiBaseUrl}/api/v1/businesses/${this.businessId}/services/${serviceId}/professionals`,
                      { professionalId },
                      { context: new HttpContext().set(REQUIRE_AUTH, true) }
                    )
                  );
                  operations.push(...addRequests);

                  // Remove professionals
                  const removeRequests = toRemove.map(professionalId =>
                    this.http.delete(
                      `${this.apiBaseUrl}/api/v1/businesses/${this.businessId}/services/${serviceId}/professionals/${professionalId}`,
                      { context: new HttpContext().set(REQUIRE_AUTH, true) }
                    )
                  );
                  operations.push(...removeRequests);

                  return operations.length > 0 ? forkJoin(operations) : of([]);
                })
              );
          })
        )
        .subscribe({
          next: () => {
            this.isSaving = false;
            // Reload services to show updated image
            this.load();
            this.afterUpdate();
          },
          error: (err) => {
            this.isSaving = false;
            this.onCreateError(err);
          },
        });
    }
  }
}
